import {
  type Attachment,
  type ExtendedAppointmentAttachment,
  type HomeProjectAttachment,
} from "@repo/api";
import { documentIcon, imageIcon } from "@repo/skapa";
import groupBy from "lodash/groupBy";

export enum FileLoadingEnum {
  DOWNLOADING_ALL = "downloading all files",
  DOWNLOADING = "downloading",
  OPENING = "opening",
  DELETING = "deleting",
  SHARING = "sharing",
}

export const getFileType = (fileName: string): string | undefined => {
  const parts = fileName.split(".");
  if (parts.length > 1) {
    return parts.pop()?.toLowerCase();
  }
  return undefined;
};

export const getFileIcon = (fileType?: string) => {
  if (
    fileType === "pdf" ||
    fileType === "docx" ||
    fileType === "txt" ||
    fileType === "csv" ||
    fileType === "xls" ||
    fileType === "xlsx" ||
    fileType === "dwg" ||
    fileType === "eml" ||
    fileType === "msg"
  ) {
    return documentIcon;
  }

  if (fileType === "jpg" || fileType === "jpeg" || fileType === "png") {
    return imageIcon;
  }

  return documentIcon;
};

export const sortProjectAttachments = (items?: Attachment[]): Attachment[] => {
  if (!items) {
    return [];
  }
  return items.sort(
    (a, b) =>
      new Date(b.uploaded_date).getTime() - new Date(a.uploaded_date).getTime()
  );
};

export const sortAppointmentAttachments = (
  items?: ExtendedAppointmentAttachment[]
): ExtendedAppointmentAttachment[] => {
  if (!items) {
    return [];
  }

  // sort by time stamp descending
  return items.sort(
    (a, b) =>
      new Date(b.currentTimestamp).getTime() -
      new Date(a.currentTimestamp).getTime()
  );
};

export const groupAppointmentAttachments = (
  items?: ExtendedAppointmentAttachment[]
): Record<string, ExtendedAppointmentAttachment[]> => {
  if (!items) {
    return {};
  }

  // group by appointment sequence number
  const groupped = groupBy(
    items ?? [],
    (item) => item.appointmentNumber
  ) as Record<string, ExtendedAppointmentAttachment[]>;

  // sort by appointment sequence number descending
  const sortedEntries = Object.entries(groupped)
    .sort(([a], [b]) => Number(b) - Number(a))
    .map(([key, items]) => [`#${key}`, sortAppointmentAttachments(items)]);
  const entries = Object.fromEntries(sortedEntries);

  return entries;
};

export const groupHomeProjectAttachments = (
  items: HomeProjectAttachment[]
): Record<string, HomeProjectAttachment[]> => {
  if (!items) {
    return {};
  }

  // group by home project id
  return groupBy(items ?? [], (item) => item.homeProjectId);
};
